import { namespace } from 'wsm-tagging-utils';

const runCustomLinkDecorator = (win) => {
	namespace(win, 'DDC.tracking.adobe_launch.decorators');
	const {
		DDC,
		DDC: { tracking }
	} = win;

	/**
	 * The Custom Link Decorator sets the proper values for the section and text properties on eventData.
	 * These properties combine to create the custom link property returned in many events.
	 *
	 * The section describes the section of the page that the event occurred on.
	 * The text describes the event or user action (ex: display text on a button).
	 *
	 * @param {Object} window The window object
	 * @param {tracking.framework.runtime.Page} page A Page instance for page related queries
	 * @param {tracking.framework.runtime.EventHandler} delegate The EventHandler implementation to which all interface operations are delegated
	 * @constructor
	 */
	tracking.adobe_launch.decorators.CustomLinkDecorator = class extends (
		tracking.framework.runtime.EventHandlerDecorator
	) {
		constructor(thisWin, page, delegate) {
			super(thisWin, page, delegate);
			this.logCategory = 'CustomLinkDecorator';
			this.OVERRIDE_TITLE = 'title';
			this.SELECT_MAKE = 'select make';

			/**
			 * Map of widget section name to selector that should define that
			 * section. Elements that match or live within one of these defined
			 * sections will be assigned that particular section. Map is iterated
			 * over so put broader selectors towards the bottom.
			 */
			this.widgetSectionSelectors = new Map([
				['datepicker', '#ui-datepicker-div'],
				['Vehicle Details Sidebar', '.vdp-sidebar'],
				['Global Nav:Viewed', '.mycars-default .menu.recent-cars'],
				[
					'Global Nav:Saved',
					'.mycars-default .menu.saved-cars, .mycars-mobile'
				],
				['Carousel', '[data-widget-name="ws-vehicle-media"]'],
				['Incentives', '[data-widget-name="incentives-search-detail"]'],
				['Footer', '.ddc-footer'],
				['Global Nav', '.page-header, .navbar, .mycars-default'],
				['More Info', '[data-widget-name="ws-content-cta"]'],
				[
					this.SELECT_MAKE,
					'.alias-sitebuilder-gm-protection-plans-1 [data-widget-id="links1"], .alias-sitebuilder-gm-protection-plans-1 [data-widget-id="links2"]'
				],
				[
					'amt',
					'.alias-sitebuilder-gm-protection-plans-1 [data-widget-id="content5"]'
				],
				[
					'inventory-listing1',
					'.inventory-listing-default [data-widget-id="inventory-listing1"]'
				]
			]);

			this.widgetTextOverride = new Map([
				[this.SELECT_MAKE, this.OVERRIDE_TITLE]
			]);

			this.arrowSelectors = {
				next: [
					'.previousNext a.next',
					"a[rel='next']",
					'.ddc-icon[class*=arrow-right]',
					'button.slick-next'
				],
				prev: [
					'.previousNext a.prev',
					"a[rel='prev']",
					'.ddc-icon[class*=arrow-left]',
					'button.slick-prev'
				]
			};

			// Handlers needing decorator applied
			this.eventHandlers = [
				'handleClickToCall',
				'handleComparePageClick',
				'handleFormChange',
				'handleFormInitiation',
				'handleFormSubmission',
				'handleGetDirections',
				'handleHproductClick',
				'handleVehicleCompareCheck',
				'handleLinkClick',
				'handleMycarsGetAlerts',
				'handleMycarsSave',
				'handleMycarsCompareCheck',
				'handleMycarsCompareSubmit',
				'handleNavigationClick',
				'handleOfferClick',
				'handleSlideshowClick',
				'handleSocialShare',
				'handleSpecialsEvent',
				'handleTypedSearch',
				'handleRecommendedVehicleClick'
			];

			this.createHandlerDecorators();
		}

		/**
		 * Determines proper custom link section value based on element.
		 *
		 * @param { Element } element Reference to element clicked
		 * @returns { string } Section of element clicked. Potential derivations of section value:
		 * 1 - Match element in widgetSectionSelectors map
		 * 2 - Dialog title, if applicable
		 * 3 - Widget heading
		 * 4 - Widget ID
		 * 5 - Element's className
		 */
		determineCustomLinkSection(element) {
			let returnValue;
			if (element instanceof Element) {
				// eslint-disable-next-line no-restricted-syntax
				for (const [sectionName, selector] of this
					.widgetSectionSelectors) {
					if (element.closest(selector) !== null) {
						return sectionName;
					}
				}

				let widget = element.closest('[data-widget-name]');
				// could be vanilla javascript (if widget.dataset is truthy) or jquery (if widget.dataset is falsy)
				if (widget) {
					// If widget is not jQuery return widget else return first element of jQuery object
					widget = widget instanceof Node ? widget : widget[0];

					const { widgetName } = widget.dataset;
					const pageContainer = DDC.client.isMobile
						? '.ddc-page'
						: '.ddc-wrapper';
					const dialogEl = element.closest('.ui-dialog');

					if (
						(dialogEl !== null ||
							widget.closest(pageContainer) !== null) &&
						widgetName
					) {
						// If event triggering element was in a dialog, first look for dialog title and use that if we can
						if (dialogEl !== null) {
							const dialogTitleEl =
								dialogEl.querySelector('.ui-dialog-title');
							if (dialogTitleEl) {
								return dialogTitleEl.innerText;
							}
						}

						// then fall back to widget heading of the widget that the event triggering element is in
						const widgetHeadingEl =
							widget.querySelector('.widget-heading');
						const widgetHeadingText =
							widgetHeadingEl && widgetHeadingEl.innerText;

						if (widgetName === 'promotions-listing') {
							// when event triggering element is a promo pagination dot, we need to append "scroller dot"
							if (element.closest('.slick-dots') !== null) {
								return `${widgetHeadingText}:scroller dot`;
							}
						}

						if (widgetHeadingText) {
							return widgetHeadingText;
						}
					}

					// fall back to widgetId if no dialog title or widgetHeading
					return widget.dataset && widget.dataset.widgetId;
				} else {
					returnValue = element.className;
				}
			}
			return returnValue;
		}

		/**
		 * Determines proper custom link text value based on element.
		 *
		 * @param { Element } element Reference to element clicked
		 * @param { String } text Text associated with the event as provided by the data layer
		 * @returns { String } Final text for click event
		 */
		determineCustomLinkText(element, text, section) {
			if (this.widgetTextOverride.get(section) === this.OVERRIDE_TITLE) {
				return $(element).attr('title');
			}
			const noText = !text || text === '#';
			const htmlPresent = !noText && text.indexOf('|') !== -1;

			if (noText || htmlPresent) {
				let newText = '';

				if (noText) {
					if (element instanceof Element) {
						// Attempt to retrieve appropriate fallback text
						const fallbackTxt =
							element.getAttribute('value') ||
							element.getAttribute('title') ||
							element.getAttribute('aria-label');
						if (fallbackTxt) {
							newText = fallbackTxt;
						} else {
							const foundKeyValuePair = Object.entries(
								this.arrowSelectors
							).find((keyValuePair) =>
								keyValuePair[1].some((selector) =>
									element.matches(selector)
								)
							);
							if (foundKeyValuePair) {
								[newText] = foundKeyValuePair;
							}
						}
					}
				} else {
					// The data layer replaces markup in text with pipes
					// that is not desired so this cleans up the pipes
					newText = text.replace(/ \| /g, ' ');
				}

				this.log.debug(
					`setting text of custom link for handleLinkClick to: ${newText}`
				);
				return newText;
			}

			return text;
		}

		determineCustomLinkLabel = function determineCustomLinkLabel(element) {
			let label = '';
			if (element && element.nodeName === 'SELECT') {
				if ($(element).siblings('label').length > 0) {
					label = $(element).siblings('label').text();
				} else {
					label = $(element).find('option:first-child').text();
				}
			}
			return label;
		};

		// Grab first href encountered in provided element.
		determineCustomLinkHref = function determineCustomLinkHref(element) {
			return $('a', element).attr('href');
		};

		// Create handler decorators from eventHandler array to add eventData.section and update eventData.text
		createHandlerDecorators() {
			this.eventHandlers.forEach((handler) => {
				this[handler] = (eventDataIn) => {
					const eventData = eventDataIn;
					const {
						element,
						section: eventDataSection,
						text
					} = eventData;
					let section;
					if (!eventDataSection) {
						section = this.determineCustomLinkSection(element);
						// Replacing possible invisible control codes such as null or newline character with an empty space test
						/* eslint-disable */
						eventData.section =
							section &&
							section.replace(/[\x00-\x1F\x7F-\x9F]/g, ' ') &&
							section.trim();
						/* eslint-enable */
						this.log.debug(
							`setting section of custom link for handleLinkClick to: ${section}`
						);
					}
					eventData.text = this.determineCustomLinkText(
						element,
						text,
						section || eventDataSection
					);
					eventData.label = this.determineCustomLinkLabel(element);
					if (typeof eventData.url === 'undefined') {
						eventData.url = this.determineCustomLinkHref(element);
						this.log.debug(
							`attempting to fill undefined url for handleLinkClick results: ${eventData.url}`
						);
					}
					this.delegate[handler](eventData);
				};
			});
		}
	};

	tracking.adobe_launch.decorators.CustomLinkDecorator.prototype.logCategory =
		'CustomLinkDecorator';
};

export default runCustomLinkDecorator;
